import { useAuth } from '@/hooks/useAuth';
import { usePlanAccess, useResourceAccess, useExecuteAccess, useSubconsPageAccess } from '@/hooks/useProjectTabsPermissions';
import { useSpecsAccess } from '@/hooks/useSpecsPermissions';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bug } from 'lucide-react';

/**
 * DebugPermissionsOverlay
 * ✅ Non-Regression: Isolated debug component, only renders when VITE_DEBUG_PERMISSIONS=true
 * ✅ Purpose: Display real-time permission check results for troubleshooting
 */
export const DebugPermissionsOverlay = () => {
  const isEnabled = import.meta.env.VITE_DEBUG_PERMISSIONS === 'true';
  
  if (!isEnabled) return null;

  return <DebugPermissionsContent />;
};

const DebugPermissionsContent = () => {
  const { user } = useAuth();
  const planAccess = usePlanAccess();
  const resourceAccess = useResourceAccess();
  const executeAccess = useExecuteAccess();
  const subconsAccess = useSubconsPageAccess();
  const specsAccess = useSpecsAccess();

  const supabaseUrl = 'https://dyakqbhcepjcommcflru.supabase.co';

  const permissions = [
    { name: 'PLAN_ACCESS', ...planAccess },
    { name: 'RESOURCE_ACCESS', ...resourceAccess },
    { name: 'EXECUTE_ACCESS', ...executeAccess },
    { name: 'SUBCONS_ACCESS', ...subconsAccess },
    { name: 'SPECS_ACCESS', ...specsAccess },
  ];

  return (
    <Card className="fixed bottom-4 right-4 w-80 max-h-96 z-[9999] shadow-lg border-2 border-orange-500 bg-background/95 backdrop-blur">
      <div className="p-3 border-b border-border bg-orange-500/10">
        <div className="flex items-center gap-2">
          <Bug className="h-4 w-4 text-orange-500" />
          <span className="font-mono text-xs font-semibold text-orange-500">DEBUG: Permissions</span>
        </div>
      </div>
      
      <ScrollArea className="h-80">
        <div className="p-3 space-y-3">
          {/* User Info */}
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground">User</div>
            <div className="font-mono text-xs break-all">{user?.email || 'N/A'}</div>
            <div className="font-mono text-[10px] text-muted-foreground break-all">
              ID: {user?.id?.slice(0, 8) || 'N/A'}...
            </div>
          </div>

          {/* Supabase URL */}
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground">Supabase URL</div>
            <div className="font-mono text-[10px] break-all text-muted-foreground">{supabaseUrl}</div>
          </div>

          {/* Permission Checks */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-muted-foreground">Permission Checks</div>
            {permissions.map((perm) => (
              <div key={perm.name} className="flex items-center justify-between gap-2 p-2 rounded bg-muted/30">
                <span className="font-mono text-xs text-foreground">{perm.name}</span>
                <div className="flex items-center gap-1">
                  {perm.isLoading ? (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">Loading</Badge>
                  ) : (
                    <Badge 
                      variant={perm.data ? "default" : "destructive"} 
                      className="text-[10px] px-1.5 py-0"
                    >
                      {perm.data ? 'TRUE' : 'FALSE'}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </ScrollArea>
    </Card>
  );
};
